import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import { fetchUserAttributes } from 'aws-amplify/auth';

import { useAuthContext } from './AuthContext';

export interface UserProfile {
  username?: string;
}

export interface UserProfileContextType {
  profile: UserProfile | null;
  setProfile: (profile: UserProfile | null) => void;
}

// Create user profile context with undefined default value
export const UserProfileContext = createContext<UserProfileContextType>({
  profile: null,
  setProfile: () => {},
});

/**
 * UserProfileProvider: Manages user profile state and provides user profile context to the application
 * Wraps the entire app to ensure user profile state is available everywhere
 * @param children - The child components to wrap
 * @returns The UserProfileProvider component
 */
export const UserProfileProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuthContext();
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      const attributes = await fetchUserAttributes();
      let username = '';
      if (attributes) {
        if (attributes['preferred_username']) username = attributes['preferred_username'];
      }
      setProfile({
        username,
      });
    };
    fetchProfile();
  }, [user]);

  return (
    <UserProfileContext.Provider value={{ profile, setProfile }}>
      {children}
    </UserProfileContext.Provider>
  );
};

/**
 * Custom hook to access user profile context
 * Throws error if used outside of UserProfileProvider
 */
export const useUserProfileContext = () => useContext(UserProfileContext);
